/** Mission 5's pipeline and its pumping station, and the bridge that carries a road over water. */

import type { Pcg32 } from '../../sim/rng';
import { HAZARDS } from '../../sim/tuning';
import type { Palette } from '../palette';
import type { KitBuilder } from './flora';
import { MeshBuilder, place } from './MeshBuilder';
import { snowSlab } from './snow';

const TAU = Math.PI * 2;

/** Pipe centre height and radius: shared so the station's stubs line up with the run. */
export const PIPE_Y = 0.75;
export const PIPE_R = 0.4;
/**
 * Deck of a bridge: half-width across the run, and how far its top stands above the banks. The deck IS the
 * crossing: it covers the whole lane the sim lets people walk (`HAZARDS.crossingHalfWidth` either side of the
 * crossing's x) and 20 cm more for the rails, so nobody ever walks on water beside it.
 */
export const BRIDGE_HALF_WIDTH: number = HAZARDS.crossingHalfWidth + 0.2;
export const BRIDGE_DECK_Y = 0.15;
/** Thickness of the deck's planking: its underside is what a `bridgePier` is drawn up to (boats.ts). */
export const BRIDGE_DECK_THICKNESS = 0.3;

/** A pipe lying along x from `x0` to `x1`. */
function pipeAlongX(mb: MeshBuilder, p: Palette, x0: number, x1: number, r: number, ramp = 'metal.mid'): void {
  mb.cylinder(r, r, x1 - x0, 8, p.color(ramp), place(x0, PIPE_Y, 0, 0, 1, 0, -Math.PI / 2), p.color('metal.lo'));
}

/**
 * Pumping station: "so heavily armoured that only a time bomb works" (02 §7). Concrete block
 * under an overhanging slab, steel plate bolted over the front, the pipeline entering both
 * ends, and a red valve wheel on the roof - the detail that says what the building is for.
 */
export const pumpStation: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  const lo = p.color('concrete.lo');
  mb.box(5.0, 2.3, 4.4, { all: p.color('concrete.mid'), left: lo, right: lo });
  mb.box(5.4, 0.3, 4.8, { all: p.color('concrete.hi'), top: p.color('concrete.mid') }, place(0, 2.3, 0));
  snowSlab(mb, p, 5.4, 4.8, 2.6);
  // Armour: three plates with gaps between, under a warning band.
  const plate = { all: p.color('metal.lo'), top: p.color('metal.mid') };
  for (const x of [-1.6, 0, 1.6]) mb.box(1.4, 1.5, 0.12, plate, place(x, 0.15, 2.24));
  mb.box(5.02, 0.22, 0.1, p.color('enemyAccent.0'), place(0, 1.85, 2.22));
  // Pipe stubs with heavy flanges where they pass through the end walls.
  for (const x0 of [-3.0, 2.4]) pipeAlongX(mb, p, x0, x0 + 0.6, PIPE_R);
  for (const x0 of [-2.72, 2.5]) pipeAlongX(mb, p, x0, x0 + 0.22, PIPE_R + 0.16, 'metal.lo');
  // Valve wheel: an octagon of bar with two spokes, lying flat on a stem so it reads from above.
  const red = p.color('enemyAccent.0');
  mb.with(place(0.9, 2.6, 0.5), () => {
    mb.cylinder(0.1, 0.1, 0.55, 5, p.color('metal.lo'));
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * TAU;
      mb.box(0.56, 0.1, 0.12, red, place(Math.sin(a) * 0.65, 0.55, Math.cos(a) * 0.65, a));
    }
    mb.box(1.3, 0.08, 0.1, red, place(0, 0.56, 0));
    mb.box(0.1, 0.08, 1.3, red, place(0, 0.56, 0));
  });
  mb.cylinder(0.34, 0.34, 0.5, 6, p.color('metal.mid'), place(-1.5, 2.6, -1.2), p.color('ink.1')); // vent
  return mb;
};

/**
 * Pipeline along x: a waist-high pipe on a concrete cradle every 4 m. Each cradle carries a
 * white collar - the "marked supports" where there is room to crawl under (03 §2.5).
 */
export function pipelineRun(p: Palette, _rng: Pcg32, length = 8): MeshBuilder {
  const mb = new MeshBuilder();
  pipeAlongX(mb, p, -length / 2, length / 2, PIPE_R);
  const bays = Math.max(1, Math.round(length / 4));
  const bay = length / bays;
  for (let i = 0; i < bays; i++) {
    const x = -length / 2 + bay * (i + 0.5);
    mb.box(0.5, PIPE_Y - 0.1, 0.9, { all: p.color('concrete.lo'), top: p.color('concrete.mid') }, place(x, 0, 0));
    pipeAlongX(mb, p, x - 0.12, x + 0.12, PIPE_R + 0.05, 'paper.0');
    // Arctic: snow lies along part of each bay's crown - patches, never one white stripe.
    if (p.biome === 'arctic') {
      mb.box(bay / 2 - 0.5, 0.08, 0.34, p.color('ground.3'), place(x + bay / 4 + 0.15, PIPE_Y + PIPE_R - 0.02, 0));
    }
  }
  return mb;
}

/**
 * Bridge along x (turn it with yaw to cross an east-west river): plank deck, low rails that
 * do not hide a man, piers running down into the water. The model's y = 0 is the BANK, not
 * the river bed - StaticWorld seats it on its abutments and lifts walkers onto the deck.
 */
export function bridgeRun(p: Palette, _rng: Pcg32, length = 12): MeshBuilder {
  const mb = new MeshBuilder();
  const width = BRIDGE_HALF_WIDTH * 2;
  const planks = { all: p.color('timber.lo'), top: p.color('timber.mid') };
  mb.box(length, BRIDGE_DECK_THICKNESS, width, planks, place(0, BRIDGE_DECK_Y - BRIDGE_DECK_THICKNESS, 0));
  for (const sz of [-1, 1]) {
    const z = sz * (BRIDGE_HALF_WIDTH - 0.1);
    mb.box(length, 0.1, 0.14, p.color('timber.hi'), place(0, BRIDGE_DECK_Y + 0.6, z));
    const posts = Math.max(1, Math.round(length / 2));
    for (let i = 0; i <= posts; i++) {
      const x = -length / 2 + 0.1 + ((length - 0.2) * i) / posts;
      mb.box(0.16, 0.62, 0.16, p.color('timber.lo'), place(x, BRIDGE_DECK_Y, z));
    }
    // Arctic: snow banked along the rails; the middle is trodden clear.
    const drift = sz * (BRIDGE_HALF_WIDTH - 0.65);
    if (p.biome === 'arctic') mb.box(length - 0.6, 0.07, 0.5, p.color('ground.3'), place(0, BRIDGE_DECK_Y, drift));
  }
  const piers = Math.max(0, Math.round(length / 4) - 1);
  for (let i = 1; i <= piers; i++) {
    const x = -length / 2 + (length * i) / (piers + 1);
    mb.box(0.5, 1.3, width - 0.4, p.color('timber.lo'), place(x, BRIDGE_DECK_Y - 1.6, 0));
  }
  return mb;
}
